import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Grid,
  Card,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  Select,
  MenuItem,
  Pagination,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Timeline as TimelineIcon,
  Science as ScienceIcon,
  Close as CloseIcon,
  Biotech as BiotechIcon,
  HealthAndSafety as HealthIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  SwapVert as SwapVertIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import LoaderOverlay from 'components/LoaderOverlay';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import ivfAPI from '../../api/ivfAPI';
import { api } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { useListUrlState } from '../../hooks/useListUrlState';
import CreateProcedureDialog, { getSubTypeOptions } from './CreateProcedureDialog';

const PROCEDURE_TYPES = ['IVF', 'IUI', 'ICSI', 'FET', 'OI', 'DONOR', 'SURROGACY', 'OTHER'];

function IVFProcedures() {
  const navigate = useNavigate();
  const theme = useTheme();
  const primaryColor = theme.palette.logo.main;
  const primaryHoverColor = theme.palette.logo.state;
  const textColor = theme.palette.text.main;
  const secondaryColor = theme.palette.secondary.main;

  const { hasPermission } = usePermissions();
  const canSearch = hasPermission('v1-ivf-search-patient');
  const canCreate = hasPermission('v1-ivf-create-draft-procedure');
  const canEdit = hasPermission('v1-ivf-update-procedure');
  const canDelete = hasPermission('v1-ivf-delete-procedure');
  const canView = hasPermission('v1-ivf-get-procedure-details');

  // Two independent filter/list sections on this page (Patient Search and
  // Procedure List below) share the URL query string — writeParams merges
  // by key so one section's writes don't clobber the other's.
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  // Search states
  const [searchTerm, setSearchTerm] = useState(urlParams?.get('search') || '');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searched, setSearched] = useState(false);

  // Pagination states
  const [page, setPage] = useState((urlParams && parseInt(urlParams.get('page'), 10)) || 1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Dialog states
  const [openProcedureDialog, setOpenProcedureDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedProcedureType, setSelectedProcedureType] = useState('');

  // Procedure list states
  const [patientProcedures, setPatientProcedures] = useState([]);
  const [loadingProcedures, setLoadingProcedures] = useState(false);

  // Edit procedure states
  const [editProcedureData, setEditProcedureData] = useState(null); // { id, procedure_type, procedure_status }
  const [savingEdit, setSavingEdit] = useState(false);

  // Change status dialog
  const [changeStatusDialog, setChangeStatusDialog] = useState({ open: false, procedureId: null, procedureCode: '', currentStatus: '' });
  const [savingStatus, setSavingStatus] = useState(false);

  // Procedure types state
  const [procedureTypes, setProcedureTypes] = useState([]);
  const [loadingProcedureTypes, setLoadingProcedureTypes] = useState(false);
  const [selectedProcedureSubType, setSelectedProcedureSubType] = useState('');

  // Default date helpers for procedure listing
  const DEFAULT_DATE_FROM = dayjs().subtract(1, 'month');
  const DEFAULT_DATE_TO = dayjs();

  // Procedure listing states
  const [procSearch, setProcSearch] = useState(() => (urlParams ? {
    procedure_code: urlParams.get('proc_code') || '',
    patient_code: urlParams.get('proc_patient_code') || '',
    status: urlParams.has('proc_status') ? urlParams.get('proc_status') : 'CREATED',
    procedure_type: urlParams.get('proc_type') || '',
    procedure_sub_type: urlParams.get('proc_sub_type') || '',
    date_from: urlParams.get('proc_date_from') ? dayjs(urlParams.get('proc_date_from')) : DEFAULT_DATE_FROM,
    date_to: urlParams.get('proc_date_to') ? dayjs(urlParams.get('proc_date_to')) : DEFAULT_DATE_TO,
  } : { procedure_code: '', patient_code: '', status: 'CREATED', procedure_type: '', procedure_sub_type: '', date_from: DEFAULT_DATE_FROM, date_to: DEFAULT_DATE_TO }));
  const [procResults, setProcResults] = useState([]);
  const [procLoading, setProcLoading] = useState(false);
  const [procSearched, setProcSearched] = useState(false);
  const [procPage, setProcPage] = useState((urlParams && parseInt(urlParams.get('proc_page'), 10)) || 1);
  const [procTotal, setProcTotal] = useState(0);
  const procItemsPerPage = 10;

  const fetchProcedureList = async (pageNum = 1, overrideParams) => {
    setProcLoading(true);
    try {
      const params = overrideParams || procSearch;
      const toDateStr = (v) => v ? (typeof v === 'string' ? v : v.format('YYYY-MM-DD')) : undefined;
      writeParams({
        proc_code: params.procedure_code,
        proc_patient_code: params.patient_code,
        proc_status: params.status,
        proc_type: params.procedure_type,
        proc_sub_type: params.procedure_sub_type,
        proc_date_from: toDateStr(params.date_from),
        proc_date_to: toDateStr(params.date_to),
        proc_page: pageNum,
      });
      const response = await ivfAPI.searchProcedures({
        ...params,
        date_from: toDateStr(params.date_from),
        date_to: toDateStr(params.date_to),
        page: pageNum,
        limit: procItemsPerPage,
      });
      if (response.flag === 1) {
        setProcResults(response.data?.data || []);
        setProcTotal(response.data?.total_count || 0);
        setProcPage(pageNum);
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: response.message || 'Failed to fetch procedures' });
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch procedures' });
    } finally {
      setProcLoading(false);
    }
  };

  const handleProcSearch = async () => {
    setProcSearched(true);
    await fetchProcedureList(1);
  };

  const handleProcPageChange = async (_, value) => {
    await fetchProcedureList(value);
  };

  // Auto-load procedure list on mount — using procSearch/procPage as they
  // are right now, which already reflect anything restored from the URL.
  useEffect(() => {
    setProcSearched(true);
    fetchProcedureList(procPage, procSearch);
    if (searchTerm.trim()) {
      setSearched(true);
      fetchPatients(page);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch patients with API-based pagination
  const fetchPatients = async (pageNum = 1) => {
    if (!searchTerm.trim()) {
      return;
    }

    setLoading(true);

    try {
      writeParams({ search: searchTerm.trim(), page: pageNum });
      const offset = (pageNum - 1) * itemsPerPage;
      const response = await ivfAPI.searchPatients(
        searchTerm.trim(),
        itemsPerPage,
        offset
      );

      if (response.flag === 1) {
        const patients = response.data?.data || [];
        const count = response.data?.total_count || 0;
        
        setSearchResults(patients);
        setTotalCount(count);
        setPage(pageNum);
        
        if (patients.length === 0 && pageNum === 1) {
          Swal.fire({
            icon: 'info',
            title: 'No Results',
            text: 'No patients found matching your search criteria',
          });
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Search Failed',
          text: response.message || 'Failed to search patients',
        });
      }
    } catch (error) {
      console.error('Error searching patients:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to search patients. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Empty Search',
        text: 'Please enter mobile number, name, or patient code',
      });
      return;
    }

    setSearched(true);
    await fetchPatients(1); // Fetch first page
  };

  const handlePageChange = async (event, value) => {
    await fetchPatients(value);
  };

  // Results from current page
  const paginatedResults = searchResults;

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleOpenProcedureDialog = async (patient) => {
    setSelectedPatient(patient);
    setOpenProcedureDialog(true);
    setEditProcedureData(null);

    // Fetch existing procedures for this patient
    setLoadingProcedures(true);
    try {
      const response = await ivfAPI.getProceduresByContact(patient.id);
      if (response.flag === 1) {
        setPatientProcedures(response.data || []);
      } else {
        setPatientProcedures([]);
      }
    } catch (error) {
      console.error('Error fetching procedures:', error);
      setPatientProcedures([]);
    } finally {
      setLoadingProcedures(false);
    }
  };

  const handleOpenCreateDialog = async (patient) => {
    setSelectedPatient(patient);
    setSelectedProcedureType('');
    setOpenCreateDialog(true);
    setLoadingProcedureTypes(true);
    try {
      const typesResponse = await ivfAPI.getProcedureTypes();
      if (typesResponse.flag === 1) {
        const types = typesResponse.data || [];
        setProcedureTypes(types);
        if (types.length > 0) setSelectedProcedureType(types[0]);
      }
    } catch (error) {
      console.error('Error fetching procedure types:', error);
    } finally {
      setLoadingProcedureTypes(false);
    }
  };

  const handleCreateProcedure = async () => {
    if (!selectedPatient) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Patient information is missing',
      });
      return;
    }

    if (!selectedProcedureType) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Selection',
        text: 'Please select a procedure type',
      });
      return;
    }

    if (!selectedProcedureSubType) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Selection',
        text: 'Please select a procedure sub-type',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await ivfAPI.createDraftProcedure(
        selectedPatient.id,
        selectedProcedureType,
        selectedProcedureSubType || 'SELF'
      );

      if (response.flag === 1 || response.statusCode === 201) {
        const procedure = response.data;
        const patientForNav = { ...selectedPatient };

        // Close the dialog immediately
        handleCloseCreateDialog();

        if (selectedProcedureType === 'IVF') {
          // IVF → redirect to initiation page
          Swal.fire({
            icon: 'success',
            title: 'Procedure Created Successfully',
            html: `<p>Procedure <strong>${procedure?.external_code || procedure?.procedure_type || ''}</strong> has been created.</p><p>Redirecting to Procedure Initiation...</p>`,
            timer: 4000,
            timerProgressBar: true,
            showConfirmButton: false,
            allowOutsideClick: false,
          }).then(() => {
            navigate(`/ivf/initiation/${procedure?.id}`, {
              state: { procedure, patient: patientForNav },
            });
          });
        } else {
          // Other types → just show success, stay on page
          Swal.fire({
            icon: 'success',
            title: 'Procedure Created Successfully',
            html: `<p>Procedure <strong>${procedure?.external_code || procedure?.procedure_type || ''}</strong> has been created.</p>`,
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: response.message || 'Failed to create procedure',
        });
      }
    } catch (error) {
      console.error('Error creating procedure:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to create procedure. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseProcedureDialog = () => {
    setOpenProcedureDialog(false);
    setSelectedPatient(null);
    setPatientProcedures([]);
    setEditProcedureData(null);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setSelectedPatient(null);
    setSelectedProcedureType('');
    setSelectedProcedureSubType('');
    setProcedureTypes([]);
  };

  const handleEditProcedure = (procedure) => {
    setEditProcedureData({
      id: procedure.id,
      procedure_type: procedure.procedure_type,
      procedure_sub_type: procedure.procedure_sub_type || 'SELF',
      procedure_status: procedure.procedure_status,
    });
  };

  const handleSaveEditProcedure = async () => {
    if (!editProcedureData) return;
    setSavingEdit(true);
    try {
      const res = await api.ivf.updateProcedure(editProcedureData.id, {
        procedure_type: editProcedureData.procedure_type,
        procedure_sub_type: editProcedureData.procedure_sub_type,
        procedure_status: editProcedureData.procedure_status,
      });
      if (res?.flag === 1 || res?.statusCode === 200) {
        setPatientProcedures(prev =>
          prev.map(p => p.id === editProcedureData.id ? { ...p, ...editProcedureData } : p)
        );
        setEditProcedureData(null);
        Swal.fire({ icon: 'success', title: 'Updated', text: 'Procedure updated successfully.', timer: 2000, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res?.message || 'Failed to update procedure.' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.message || 'Failed to update procedure.' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteProcedure = async (procedure) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Procedure',
      text: `Delete procedure "${procedure.external_code || procedure.procedure_type}"? This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      didOpen: () => {
        // Ensure Swal appears above MUI Dialog (z-index 1300)
        const container = document.querySelector('.swal2-container');
        if (container) container.style.zIndex = '1400';
      },
    });
    if (!result.isConfirmed) return;
    try {
      const res = await api.ivf.deleteProcedure(procedure.id);
      if (res?.flag === 1 || res?.statusCode === 200) {
        setPatientProcedures(prev => prev.filter(p => p.id !== procedure.id));
        Swal.fire({ icon: 'success', title: 'Deleted', text: 'Procedure deleted.', timer: 2000, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res?.message || 'Failed to delete procedure.' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.message || 'Failed to delete procedure.' });
    }
  };

  const handleViewProcedureDetails = (procedure) => {
    navigate(`/ivf/procedure/${procedure.id}`, {
      state: { procedure, patient: selectedPatient },
    });
  };

  const handleOpenChangeStatus = (proc) => {
    setChangeStatusDialog({ open: true, procedureId: proc.id, procedureCode: proc.external_code || `#${proc.id}`, currentStatus: proc.procedure_status });
  };

  const handleCloseChangeStatus = () => {
    if (savingStatus) return;
    setChangeStatusDialog({ open: false, procedureId: null, procedureCode: '', currentStatus: '' });
  };

  const handleSaveStatus = async () => {
    if (!changeStatusDialog.procedureId) return;
    setSavingStatus(true);
    try {
      const res = await api.ivf.updateProcedure(changeStatusDialog.procedureId, {
        procedure_status: changeStatusDialog.currentStatus,
      });
      if (res?.flag === 1 || res?.statusCode === 200) {
        setPatientProcedures(prev =>
          prev.map(p => p.id === changeStatusDialog.procedureId ? { ...p, procedure_status: changeStatusDialog.currentStatus } : p)
        );
        handleCloseChangeStatus();
        Swal.fire({ icon: 'success', title: 'Status Updated', text: 'Procedure status updated successfully.', timer: 2000, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res?.message || 'Failed to update status.' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.message || 'Failed to update status.' });
    } finally {
      setSavingStatus(false);
    }
  };

  const getStatusStyle = (status) => {
    const map = {
      CREATED:   { backgroundColor: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
      ONGOING:   { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
      COMPLETED: { backgroundColor: '#ede7f6', color: '#4527a0', border: '#ce93d8' },
      CANCELLED: { backgroundColor: '#ffebee', color: '#b71c1c', border: '#ef9a9a' },
      ON_HOLD:   { backgroundColor: '#fff3e0', color: '#e65100', border: '#ffe0b2' },
    };
    return map[status] || { backgroundColor: '#f5f5f5', color: '#424242', border: '#dee2e6' };
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />

      <MDBox p={3} sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        {/* Professional Breadcrumb */}
        <MDBox mb={3}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" sx={{ color: secondaryColor }} />}
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                mx: 1
              },
              backgroundColor: 'white',
              px: 2,
              py: 1.5,
              borderRadius: 1,
              border: '1px solid #e9ecef'
            }}
          >
            <Link
              underline="hover"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: secondaryColor,
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': { color: primaryColor },
                transition: 'color 0.2s ease'
              }}
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
              Dashboard
            </Link>
            <Typography 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: primaryColor,
                fontWeight: 700,
                fontSize: '0.875rem',
                letterSpacing: '0.5px'
              }}
            >
              <ScienceIcon sx={{ mr: 0.5, fontSize: 16 }} />
              IVF PROCEDURES MANAGEMENT
            </Typography>
          </Breadcrumbs>
        </MDBox>

        {/* Corporate Header */}
        <Card
          sx={{
            mb: 4,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            border: '1px solid #e9ecef',
            borderRadius: 2
          }}
        >
          <Box sx={{ p: 3, borderBottom: '1px solid #e9ecef', backgroundColor: 'white' }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <ScienceIcon sx={{ fontSize: 28, color: secondaryColor }} />
                </Box>
              </Grid>
              <Grid item xs>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1.75rem',
                    color: textColor,
                    mb: 0.5
                  }}
                >
                  IVF Procedures Management System
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: secondaryColor,
                    fontSize: '1rem',
                    fontWeight: 400
                  }}
                >
                  Comprehensive fertility treatment management and patient procedure tracking
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Card>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Left Column: Search and Results */}
          <Grid item xs={12} lg={8}>
            {/* Professional Search Card */}
            <Card 
              sx={{ 
                mb: 3,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef',
                borderRadius: 2
              }}
            >
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: textColor, fontSize: '1.125rem' }}>
                  Patient Search
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={9}>
                    <TextField
                      fullWidth
                      label="Search Patient"
                      placeholder="Mobile number, patient name, or patient code"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                      variant="outlined"
                      size="medium"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover fieldset': {
                            borderColor: primaryColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: primaryColor,
                            borderWidth: 2,
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: primaryColor
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box display="flex" flexDirection="row" gap={1} height="100%">
                      <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={handleSearch}
                        disabled={loading}
                        fullWidth
                        sx={{
                          padding: "10px 16px",
                          background: primaryColor,
                          color: "#fff",
                          borderRadius: 1,
                          fontSize: "0.95rem",
                          fontWeight: "500",
                          height: 56,
                          '&:hover': {
                            background: primaryHoverColor,
                          },
                        }}
                      >
                        Search
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>

            {/* Professional Results Card */}
            <Card 
              sx={{ 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef',
                borderRadius: 2
              }}
            >
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 600, color: textColor, fontSize: '1.125rem' }}>
                    Search Results
                  </Typography>
                  {searchResults.length > 0 && (
                    <Chip 
                      label={`${searchResults.length} patients found`} 
                      variant="outlined"
                      sx={{ 
                        borderColor: primaryColor,
                        color: primaryColor,
                        fontWeight: 500
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Box sx={{ p: 3 }}>
                {!searched ? (
                  <Box 
                    textAlign="center" 
                    py={6}
                    sx={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: 2,
                      border: '1px dashed #dee2e6'
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 48, color: '#adb5bd', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: textColor, mb: 1 }}>
                      Start Patient Search
                    </Typography>
                    <Typography color="textSecondary" variant="body2">
                      Use the search field above to find patients for IVF procedure management
                    </Typography>
                  </Box>
                ) : searchResults.length === 0 && !loading ? (
                  <Box 
                    textAlign="center" 
                    py={6}
                    sx={{
                      backgroundColor: '#fff3cd',
                      borderRadius: 2,
                      border: '1px solid #ffeaa7',
                      color: '#856404'
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 48, color: '#856404', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      No Patients Found
                    </Typography>
                    <Typography variant="body2">
                      Please refine your search criteria and try again
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Professional Patient Table */}
                    <TableContainer>
                      <Table>
                        <TableBody>
                          <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f0f2f5' }}>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.875rem' }}>Patient Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.875rem' }}>Patient Code</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.875rem' }}>Mobile</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.875rem' }} align="center">Actions</TableCell>
                          </TableRow>
                          {paginatedResults.map((patient) => (
                            <TableRow 
                              key={patient.id}
                              sx={{ 
                                '&:hover': { 
                                  backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa'
                                },
                                borderBottom: `1px solid ${theme.palette.divider}`
                              }}
                            >
                              <TableCell>
                                <Box display="flex" alignItems="flex-start" gap={2}>
                                  <Avatar 
                                    src={patient.profile_picture || ''}
                                    sx={{ 
                                      backgroundColor: primaryColor,
                                      width: 56,
                                      height: 56,
                                      flexShrink: 0,
                                      fontSize: '1.25rem',
                                      fontWeight: 600
                                    }}
                                  >
                                    {!patient.profile_picture && patient.full_name ? patient.full_name.charAt(0).toUpperCase() : <PersonIcon />}
                                  </Avatar>
                                  <Box flex={1}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: textColor, mb: 0.5 }}>
                                      {patient.full_name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: secondaryColor, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <PhoneIcon sx={{ fontSize: 14 }} />
                                      {patient.mobile_number || 'N/A'}
                                    </Typography>
                                    {patient.email_address && (
                                      <Typography variant="caption" sx={{ color: secondaryColor, display: 'block', mt: 0.3 }}>
                                        {patient.email_address}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={patient.external_code || patient.patient_code || 'N/A'} 
                                  variant="outlined"
                                  size="small"
                                  sx={{ 
                                    borderColor: secondaryColor,
                                    color: secondaryColor,
                                    fontWeight: 500
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: textColor }}>
                                  {patient.mobile_number || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Box display="flex" gap={1} justifyContent="center">
                                  {canCreate && (
                                    <Tooltip title="Create Procedure">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenCreateDialog(patient)}
                                        sx={{
                                          backgroundColor: primaryColor,
                                          color: 'white',
                                          '&:hover': {
                                            backgroundColor: primaryHoverColor,
                                          }
                                        }}
                                      >
                                        <AddIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Manage Procedures">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenProcedureDialog(patient)}
                                      sx={{
                                        backgroundColor: secondaryColor,
                                        color: 'white',
                                        '&:hover': {
                                          backgroundColor: '#5a9d5f',
                                        }
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Clean Pagination */}
                    {totalPages > 1 && (
                      <Box 
                        display="flex" 
                        justifyContent="center" 
                        mt={3}
                        pt={2}
                        sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                      >
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={handlePageChange}
                          color="primary"
                          sx={{
                            '& .MuiPaginationItem-root': {
                              color: secondaryColor,
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: '#e9ecef'
                              }
                            },
                            '& .Mui-selected': {
                              backgroundColor: primaryColor,
                              color: 'white',
                              '&:hover': {
                                backgroundColor: primaryHoverColor
                              }
                            }
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Card>

            {/* Procedure Listing Card */}
            <Card
              sx={{
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef',
                borderRadius: 2,
                mt: 3,
              }}
            >
              {/* Header */}
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: textColor, fontSize: '1.125rem' }}>
                  Procedure List
                </Typography>
              </Box>

              {/* Filters */}
              <Box sx={{ p: 2, borderBottom: '1px solid #e9ecef' }}>
                <Grid container spacing={2} alignItems="center">
                  {/* Row 1: Procedure Code + Patient Code/Name — 50/50 full width */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Procedure Code"
                      value={procSearch.procedure_code}
                      onChange={(e) => setProcSearch(prev => ({ ...prev, procedure_code: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleProcSearch()}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                        '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Patient Code / Name"
                      value={procSearch.patient_code}
                      onChange={(e) => setProcSearch(prev => ({ ...prev, patient_code: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleProcSearch()}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                        '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
                      }}
                    />
                  </Grid>

                  {/* Row 2: Status — full width */}
                  <Grid item xs={12}>
                    <Select
                      value={procSearch.status}
                      onChange={(e) => setProcSearch(prev => ({ ...prev, status: e.target.value }))}
                      size="small"
                      fullWidth
                      displayEmpty
                      sx={{
                        height: '45px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                      }}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      {['CREATED', 'ONGOING', 'COMPLETED', 'CANCELLED', 'ON_HOLD'].map(s => (
                        <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  {/* Row 2b: Type + Sub-type — 50/50 */}
                  <Grid item xs={12} sm={6}>
                    <Select
                      value={procSearch.procedure_type}
                      onChange={(e) => {
                        const t = e.target.value;
                        setProcSearch(prev => ({ ...prev, procedure_type: t, procedure_sub_type: '' }));
                      }}
                      size="small"
                      fullWidth
                      displayEmpty
                      sx={{
                        height: '45px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                      }}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      {PROCEDURE_TYPES.map(t => (
                        <MenuItem key={t} value={t}>{t}</MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Select
                      value={procSearch.procedure_sub_type}
                      onChange={(e) => setProcSearch(prev => ({ ...prev, procedure_sub_type: e.target.value }))}
                      size="small"
                      fullWidth
                      displayEmpty
                      disabled={!procSearch.procedure_type}
                      sx={{
                        height: '45px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                      }}
                    >
                      <MenuItem value="">All Sub-types</MenuItem>
                      {getSubTypeOptions(procSearch.procedure_type).map(st => (
                        <MenuItem key={st} value={st}>{st}</MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  {/* Row 3: From Date + To Date — 50/50 */}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="From Date"
                        value={procSearch.date_from}
                        onChange={(newVal) => setProcSearch(prev => ({ ...prev, date_from: newVal }))}
                        inputFormat="DD/MM/YYYY"
                        maxDate={procSearch.date_to || undefined}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                              '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="To Date"
                        value={procSearch.date_to}
                        onChange={(newVal) => setProcSearch(prev => ({ ...prev, date_to: newVal }))}
                        inputFormat="DD/MM/YYYY"
                        minDate={procSearch.date_from || undefined}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: primaryColor },
                              '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </LocalizationProvider>

                  {/* Row 4: Search button */}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleProcSearch}
                      disabled={procLoading}
                      sx={{
                        background: primaryColor,
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        '&:hover': { background: primaryHoverColor },
                      }}
                    >
                      Search Procedures
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Results */}
              <Box sx={{ p: 2 }}>
                {!procSearched ? (
                  <Box textAlign="center" py={5} sx={{ backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px dashed #dee2e6' }}>
                    <ScienceIcon sx={{ fontSize: 40, color: '#adb5bd', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: secondaryColor }}>
                      Use filters above to search procedures
                    </Typography>
                  </Box>
                ) : procLoading ? (
                  <Box display="flex" justifyContent="center" py={5}>
                    <CircularProgress size={30} sx={{ color: primaryColor }} />
                  </Box>
                ) : procResults.length === 0 ? (
                  <Box textAlign="center" py={5} sx={{ backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffeaa7' }}>
                    <Typography variant="body2" sx={{ color: '#856404' }}>No procedures found</Typography>
                  </Box>
                ) : (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                        {procTotal} procedure{procTotal !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f0f2f5' }}>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.8rem' }}>Procedure Code</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.8rem' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.8rem' }}>Sub-type</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.8rem' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.8rem' }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: primaryColor, fontSize: '0.8rem' }} align="center">Actions</TableCell>
                          </TableRow>
                          {procResults.map((proc) => {
                            const statusStyle = getStatusStyle(proc.procedure_status);
                            return (
                              <TableRow
                                key={proc.id}
                                sx={{
                                  '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa' },
                                  borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: textColor, fontSize: '0.8rem' }}>
                                    {proc.external_code || `#${proc.id}`}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={proc.procedure_type}
                                    size="small"
                                    sx={{
                                      height: 20, fontSize: '0.68rem', fontWeight: 700,
                                      backgroundColor: 'rgba(0,121,142,0.09)',
                                      color: primaryColor,
                                      border: `1px solid rgba(0,121,142,0.22)`,
                                      '& .MuiChip-label': { px: 0.9 },
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {proc.procedure_sub_type ? (
                                    <Chip
                                      label={proc.procedure_sub_type}
                                      size="small"
                                      sx={{
                                        height: 20, fontSize: '0.68rem', fontWeight: 700,
                                        backgroundColor: proc.procedure_sub_type === 'SELF' ? 'rgba(56,142,60,0.09)' : 'rgba(103,58,183,0.09)',
                                        color: proc.procedure_sub_type === 'SELF' ? '#388e3c' : '#5e35b1',
                                        border: `1px solid ${proc.procedure_sub_type === 'SELF' ? 'rgba(56,142,60,0.22)' : 'rgba(103,58,183,0.22)'}`,
                                        '& .MuiChip-label': { px: 0.9 },
                                      }}
                                    />
                                  ) : <Typography variant="caption" sx={{ color: secondaryColor }}>—</Typography>}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={(proc.procedure_status || '').replace('_', ' ')}
                                    size="small"
                                    sx={{
                                      height: 20, fontSize: '0.68rem', fontWeight: 700,
                                      backgroundColor: statusStyle.backgroundColor,
                                      color: statusStyle.color,
                                      border: `1px solid ${statusStyle.border}`,
                                      '& .MuiChip-label': { px: 0.9 },
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" sx={{ color: secondaryColor, fontSize: '0.73rem' }}>
                                    {proc.created_at
                                      ? new Date(proc.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                      : '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Box display="flex" gap={0.5} justifyContent="center">
                                    <Tooltip title="Folliculometry">
                                      <IconButton
                                        size="small"
                                        onClick={() => navigate(`/ivf/folliculometry/${proc.id}`, { state: { procedure: proc } })}
                                        sx={{ color: '#00897b', backgroundColor: 'rgba(0,137,123,0.06)', '&:hover': { backgroundColor: 'rgba(0,137,123,0.16)' }, width: 28, height: 28 }}
                                      >
                                        <BiotechIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Tooltip>
                                    {proc.procedure_type === 'IVF' && (
                                      <Tooltip title="IVF Lab">
                                        <IconButton
                                          size="small"
                                          onClick={() => navigate(`/ivf/lab-performance/${proc.id}`, { state: { procedure: proc } })}
                                          sx={{ color: '#0288d1', backgroundColor: 'rgba(2,136,209,0.06)', '&:hover': { backgroundColor: 'rgba(2,136,209,0.16)' }, width: 28, height: 28 }}
                                        >
                                          <ScienceIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {canEdit && (
                                      <Tooltip title="Change Status">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenChangeStatus(proc)}
                                          sx={{ color: '#7b1fa2', backgroundColor: 'rgba(123,31,162,0.06)', '&:hover': { backgroundColor: 'rgba(123,31,162,0.16)' }, width: 28, height: 28 }}
                                        >
                                          <SwapVertIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {canEdit && !['COMPLETED', 'CANCELLED'].includes(proc.procedure_status) && (
                                      <Tooltip title="Edit Procedure">
                                        <IconButton
                                          size="small"
                                          onClick={() => navigate(`/ivf/initiation/${proc.id}`, { state: { procedure: proc } })}
                                          sx={{ color: '#f57c00', backgroundColor: 'rgba(245,124,0,0.06)', '&:hover': { backgroundColor: 'rgba(245,124,0,0.16)' }, width: 28, height: 28 }}
                                        >
                                          <EditIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {canDelete && proc.procedure_status !== 'COMPLETED' && (
                                      <Tooltip title="Delete">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteProcedure(proc)}
                                          sx={{ color: '#dc3545', backgroundColor: 'rgba(220,53,69,0.06)', '&:hover': { backgroundColor: 'rgba(220,53,69,0.16)' }, width: 28, height: 28 }}
                                        >
                                          <CloseIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {Math.ceil(procTotal / procItemsPerPage) > 1 && (
                      <Box display="flex" justifyContent="center" mt={2} pt={2} sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
                        <Pagination
                          count={Math.ceil(procTotal / procItemsPerPage)}
                          page={procPage}
                          onChange={handleProcPageChange}
                          color="primary"
                          sx={{
                            '& .MuiPaginationItem-root': {
                              color: secondaryColor,
                              fontWeight: 500,
                              '&:hover': { backgroundColor: '#e9ecef' },
                            },
                            '& .Mui-selected': {
                              backgroundColor: primaryColor,
                              color: 'white',
                              '&:hover': { backgroundColor: primaryHoverColor },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Right Column: Information Panel */}
          <Grid item xs={12} lg={4}>
            <Card 
              sx={{ 
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef',
                borderRadius: 2,
                position: 'sticky',
                top: 20
              }}
            >
              {/* Information Header */}
              <Box sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f0f2f5', borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, fontSize: '1.125rem' }}>
                  IVF Treatment Information
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                {/* Overview Section */}
                <Box mb={4}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 2, fontSize: '1rem' }}>
                    Treatment Overview
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: secondaryColor,
                      lineHeight: 1.6, 
                      mb: 3,
                      fontSize: '0.875rem'
                    }}
                  >
                    In Vitro Fertilization (IVF) is an assisted reproductive technology where fertilization 
                    occurs in a controlled laboratory environment. This procedure helps couples overcome 
                    various fertility challenges through advanced medical techniques.
                  </Typography>
                  
                  <Typography variant="body2" sx={{ fontWeight: 600, color: textColor, mb: 2, fontSize: '0.875rem' }}>
                    Available Treatment Types:
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))', gap: 1 }}>
                    {PROCEDURE_TYPES.map((type) => (
                      <Chip
                        key={type}
                        label={type}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: '#dee2e6',
                          color: '#495057',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          backgroundColor: 'white'
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                <Divider sx={{ my: 3, borderColor: theme.palette.divider }} />

                {/* Treatment Process */}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 3, fontSize: '1rem' }}>
                    Treatment Process
                  </Typography>
                  
                  <Box>
                    {[
                      {
                        step: '01',
                        title: 'Initial Consultation & Assessment',
                        description: 'Comprehensive evaluation of medical history and fertility factors'
                      },
                      {
                        step: '02',
                        title: 'Ovarian Stimulation Protocol',
                        description: 'Controlled hormone therapy to stimulate multiple egg development'
                      },
                      {
                        step: '03',
                        title: 'Monitoring & Egg Retrieval',
                        description: 'Regular monitoring followed by minor surgical egg collection procedure'
                      },
                      {
                        step: '04',
                        title: 'Laboratory Fertilization',
                        description: 'Sperm and egg combination under controlled laboratory conditions'
                      },
                      {
                        step: '05',
                        title: 'Embryo Transfer & Follow-up',
                        description: 'Embryo placement and comprehensive pregnancy monitoring'
                      }
                    ].map((item) => (
                      <Box 
                        key={item.step}
                        sx={{ 
                          mb: 3,
                          pb: 2,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          '&:last-child': {
                            borderBottom: 'none',
                            mb: 0
                          }
                        }}
                      >
                        <Box display="flex" alignItems="flex-start" gap={2}>
                          <Box
                            sx={{
                              backgroundColor: primaryColor,
                              borderRadius: '50%',
                              width: 48,
                              height: 48,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.875rem',
                              fontWeight: 700,
                              flexShrink: 0,
                              boxShadow: `0 4px 12px rgba(0, 121, 142, 0.3)`,
                              transition: 'all 0.3s ease',
                              color: '#ffffff !important',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                boxShadow: `0 6px 16px rgba(0, 121, 142, 0.4)`
                              }
                            }}
                          >
                            {item.step}
                          </Box>
                          <Box flex={1}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 600, 
                                color: textColor,
                                mb: 0.5,
                                fontSize: '0.875rem'
                              }}
                            >
                              {item.title}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: secondaryColor,
                                fontSize: '0.8125rem',
                                lineHeight: 1.5
                              }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Success Information */}
                  <Box 
                    mt={4} 
                    p={3} 
                    sx={{ 
                      backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f0f2f5',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: primaryColor, mb: 2 }}>
                      Treatment Success Factors
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: textColor,
                        fontSize: '0.8125rem',
                        lineHeight: 1.6
                      }}
                    >
                      Success rates are influenced by patient age, underlying fertility conditions, 
                      treatment history, and adherence to medical protocols. Our clinic maintains 
                      comprehensive tracking and personalized care plans to optimize outcomes.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Create Procedure Dialog */}
      <CreateProcedureDialog
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
        patient={selectedPatient}
        procedureTypes={procedureTypes}
        loadingProcedureTypes={loadingProcedureTypes}
        selectedType={selectedProcedureType}
        onTypeChange={setSelectedProcedureType}
        selectedSubType={selectedProcedureSubType}
        onSubTypeChange={setSelectedProcedureSubType}
        onSubmit={handleCreateProcedure}
        submitting={loading}
        primaryColor={primaryColor}
      />

      {/* Manage Procedures Dialog */}
      <Dialog
        open={openProcedureDialog}
        onClose={handleCloseProcedureDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.20)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh',
          }
        }}
      >
        {/* ── Header ── */}
        <Box sx={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, #005f75 100%)`,
          px: 3, pt: 2, pb: 2,
          flexShrink: 0,
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{
                width: 42, height: 42, borderRadius: '10px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <ScienceIcon sx={{ color: 'white', fontSize: '1.3rem' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '1.05rem', lineHeight: 1.3 }}>
                  Manage Procedures
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.72)', mt: 0.25 }} noWrap>
                  {selectedPatient?.full_name || selectedPatient?.name}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
              <IconButton
                onClick={handleCloseProcedureDialog}
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.12)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* ── Scrollable Body ── */}
        <Box sx={{ overflowY: 'auto', px: 3, py: 2.5, flex: 1 }}>

          {/* Procedures Listing */}
          {loadingProcedures ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={5} gap={1.5}>
              <CircularProgress size={30} sx={{ color: primaryColor }} />
              <Typography variant="body2" sx={{ color: secondaryColor }}>Loading procedures...</Typography>
            </Box>
          ) : patientProcedures.length === 0 ? (
            <Box textAlign="center" py={5}
              sx={{ backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px dashed #dee2e6' }}
            >
              <ScienceIcon sx={{ fontSize: 40, color: '#adb5bd', mb: 1.5 }} />
              <Typography variant="body1" sx={{ fontWeight: 700, color: textColor, mb: 0.5 }}>
                No Procedures Found
              </Typography>
              <Typography variant="body2" sx={{ color: secondaryColor, fontSize: '0.8125rem' }}>
                No procedures have been created for this patient yet.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="caption" sx={{
                fontWeight: 700, color: secondaryColor,
                textTransform: 'uppercase', letterSpacing: '0.6px',
                display: 'block', mb: 1.5,
              }}>
                {patientProcedures.length} Procedure{patientProcedures.length !== 1 ? 's' : ''}
              </Typography>

              {patientProcedures.map((proc) => {
                const statusStyle = getStatusStyle(proc.procedure_status);
                return (
                  <Box key={proc.id}>
                    {editProcedureData?.id === proc.id ? (
                      /* ── Inline Edit Row ── */
                      <Box sx={{
                        border: `2px solid ${primaryColor}`,
                        borderRadius: 2, p: 2, mb: 1.5,
                        backgroundColor: '#f0fafb',
                      }}>
                        <Typography variant="caption" sx={{
                          fontWeight: 700, color: primaryColor,
                          textTransform: 'uppercase', letterSpacing: '0.5px',
                          display: 'block', mb: 1.5,
                        }}>
                          Editing — {proc.external_code || `#${proc.id}`}
                        </Typography>
                        <Grid container spacing={1.5} alignItems="center">
                          <Grid item xs={5}>
                            <Select size="small" fullWidth value={editProcedureData.procedure_type}
                              sx={{ backgroundColor: 'white', fontWeight: 600, fontSize: '0.85rem' }}
                              onChange={(e) => {
                                const t = e.target.value;
                                const prevSub = prev.procedure_sub_type;
                                const opts = getSubTypeOptions(t);
                                const newSub = opts.includes(prevSub) ? prevSub : opts[0];
                                setEditProcedureData(prev => ({
                                  ...prev,
                                  procedure_type: t,
                                  procedure_sub_type: newSub,
                                }));
                              }}
                            >
                              {PROCEDURE_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontWeight: 500 }}>{t}</MenuItem>)}
                            </Select>
                          </Grid>
                          <Grid item xs={5}>
                            <Select size="small" fullWidth value={editProcedureData.procedure_status}
                              sx={{ backgroundColor: 'white', fontWeight: 600, fontSize: '0.85rem' }}
                              onChange={(e) => setEditProcedureData(prev => ({ ...prev, procedure_status: e.target.value }))}
                            >
                              {['CREATED','ONGOING','COMPLETED','CANCELLED','ON_HOLD'].map(s => (
                                <MenuItem key={s} value={s} sx={{ fontWeight: 500 }}>{s}</MenuItem>
                              ))}
                            </Select>
                          </Grid>
                          <Grid item xs={2}>
                            <Box display="flex" gap={0.5}>
                              <Tooltip title="Save">
                                <IconButton size="small" onClick={handleSaveEditProcedure} disabled={savingEdit}
                                  sx={{ color: '#28a745', '&:hover': { backgroundColor: 'rgba(40,167,69,0.1)' } }}
                                >
                                  {savingEdit ? <CircularProgress size={14} /> : <HistoryIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={() => setEditProcedureData(null)} disabled={savingEdit}
                                  sx={{ color: '#6c757d', '&:hover': { backgroundColor: 'rgba(108,117,125,0.1)' } }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Grid>
                          <Grid item xs={10}>
                            <Select size="small" fullWidth value={editProcedureData.procedure_sub_type || 'SELF'}
                              sx={{ backgroundColor: 'white', fontWeight: 600, fontSize: '0.85rem' }}
                              onChange={(e) => setEditProcedureData(prev => ({ ...prev, procedure_sub_type: e.target.value }))}
                            >
                              {getSubTypeOptions(editProcedureData.procedure_type).map(st => (
                                <MenuItem key={st} value={st} sx={{ fontWeight: 500 }}>{st}</MenuItem>
                              ))}
                            </Select>
                          </Grid>
                          <Grid item xs={2} />
                        </Grid>
                      </Box>
                    ) : (
                      /* ── Display Row ── */
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        p: 1.75, mb: 1.25,
                        border: '1.5px solid #e9ecef',
                        borderRadius: 2,
                        backgroundColor: 'white',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: primaryColor,
                          backgroundColor: '#f7fbfc',
                          boxShadow: `0 2px 8px rgba(0,121,142,0.10)`,
                        },
                      }}>
                        {/* Icon Badge */}
                        <Box sx={{
                          width: 44, height: 44, borderRadius: '10px', flexShrink: 0,
                          backgroundColor: 'rgba(0,121,142,0.08)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <ScienceIcon sx={{ color: primaryColor, fontSize: 22 }} />
                        </Box>

                        {/* Info */}
                        <Box flex={1} minWidth={0}>
                          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" mb={0.35}>
                            <Typography sx={{ fontWeight: 800, color: textColor, fontSize: '0.9rem', letterSpacing: '0.15px' }}>
                              {proc.external_code || `Procedure #${proc.id}`}
                            </Typography>
                            <Chip
                              label={proc.procedure_type}
                              size="small"
                              sx={{
                                height: 19, fontSize: '0.68rem', fontWeight: 700,
                                backgroundColor: 'rgba(0,121,142,0.09)',
                                color: primaryColor,
                                border: `1px solid rgba(0,121,142,0.22)`,
                                '& .MuiChip-label': { px: 0.9 },
                              }}
                            />
                            {proc.procedure_sub_type && proc.procedure_sub_type !== 'SELF' && (
                              <Chip
                                label={proc.procedure_sub_type}
                                size="small"
                                sx={{
                                  height: 19, fontSize: '0.68rem', fontWeight: 700,
                                  backgroundColor: 'rgba(103,58,183,0.09)',
                                  color: '#5e35b1',
                                  border: `1px solid rgba(103,58,183,0.22)`,
                                  '& .MuiChip-label': { px: 0.9 },
                                }}
                              />
                            )}
                            {proc.procedure_sub_type === 'SELF' && (
                              <Chip
                                label="SELF"
                                size="small"
                                sx={{
                                  height: 19, fontSize: '0.68rem', fontWeight: 700,
                                  backgroundColor: 'rgba(56,142,60,0.09)',
                                  color: '#388e3c',
                                  border: `1px solid rgba(56,142,60,0.22)`,
                                  '& .MuiChip-label': { px: 0.9 },
                                }}
                              />
                            )}
                            <Chip
                              label={(proc.procedure_status || '').replace('_', ' ')}
                              size="small"
                              sx={{
                                height: 19, fontSize: '0.68rem', fontWeight: 700,
                                backgroundColor: statusStyle.backgroundColor,
                                color: statusStyle.color,
                                border: `1px solid ${statusStyle.border}`,
                                '& .MuiChip-label': { px: 0.9 },
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: secondaryColor, fontSize: '0.73rem' }}>
                            Created:{' '}
                            {proc.created_at
                              ? new Date(proc.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                              : '—'}
                          </Typography>
                        </Box>

                        {/* Action Buttons */}
                        <Box display="flex" gap={0.5} flexShrink={0}>
                          <Tooltip title="Folliculometry">
                            <IconButton size="small" onClick={() => { handleCloseProcedureDialog(); navigate(`/ivf/folliculometry/${proc.id}`, { state: { procedure: proc, patient: selectedPatient } }); }}
                              sx={{
                                color: '#00897b',
                                backgroundColor: 'rgba(0,137,123,0.06)',
                                '&:hover': { backgroundColor: 'rgba(0,137,123,0.16)' },
                                width: 30, height: 30,
                              }}
                            >
                              <BiotechIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          {canEdit && (
                            <Tooltip title="Change Status">
                              <IconButton size="small" onClick={() => handleOpenChangeStatus(proc)}
                                sx={{
                                  color: '#7b1fa2',
                                  backgroundColor: 'rgba(123,31,162,0.06)',
                                  '&:hover': { backgroundColor: 'rgba(123,31,162,0.16)' },
                                  width: 30, height: 30,
                                }}
                              >
                                <SwapVertIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canEdit && !['COMPLETED', 'CANCELLED'].includes(proc.procedure_status) && (
                            <Tooltip title="Edit Procedure">
                              <IconButton size="small" onClick={() => { handleCloseProcedureDialog(); navigate(`/ivf/initiation/${proc.id}`, { state: { procedure: proc, patient: selectedPatient } }); }}
                                sx={{
                                  color: '#f57c00',
                                  backgroundColor: 'rgba(245,124,0,0.06)',
                                  '&:hover': { backgroundColor: 'rgba(245,124,0,0.16)' },
                                  width: 30, height: 30,
                                }}
                              >
                                <EditIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && proc.procedure_status !== 'COMPLETED' && (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDeleteProcedure(proc)}
                                sx={{
                                  color: '#dc3545',
                                  backgroundColor: 'rgba(220,53,69,0.06)',
                                  '&:hover': { backgroundColor: 'rgba(220,53,69,0.16)' },
                                  width: 30, height: 30,
                                }}
                              >
                                <CloseIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* ── Footer ── */}
        <Box sx={{
          px: 3, py: 2,
          borderTop: '1px solid #e9ecef',
          display: 'flex', justifyContent: 'flex-end',
          backgroundColor: '#f8f9fa',
          flexShrink: 0,
        }}>
          <Button
            onClick={handleCloseProcedureDialog}
            variant="outlined"
            sx={{
              borderColor: '#dee2e6', color: textColor,
              textTransform: 'none', fontWeight: 600,
              borderRadius: '8px', px: 3,
              '&:hover': { borderColor: primaryColor, color: primaryColor, backgroundColor: 'transparent' },
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* ── Change Status Dialog ── */}
      <Dialog
        open={changeStatusDialog.open}
        onClose={handleCloseChangeStatus}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.20)',
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          background: `linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)`,
          px: 3, pt: 2, pb: 2, flexShrink: 0,
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '9px',
                backgroundColor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <SwapVertIcon sx={{ color: 'white', fontSize: '1.1rem' }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.95rem', lineHeight: 1.3 }}>
                  Change Status
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.72)', mt: 0.1 }}>
                  {changeStatusDialog.procedureCode}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={handleCloseChangeStatus}
              disabled={savingStatus}
              size="small"
              sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.12)' } }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ px: 3, py: 2.5 }}>
          <Typography variant="caption" sx={{
            fontWeight: 700, color: textColor, display: 'block', mb: 1,
            textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.72rem',
          }}>
            New Status
          </Typography>
          <Select
            value={changeStatusDialog.currentStatus}
            onChange={(e) => setChangeStatusDialog(prev => ({ ...prev, currentStatus: e.target.value }))}
            fullWidth
            size="small"
            sx={{
              backgroundColor: 'white',
              fontWeight: 600,
              fontSize: '0.875rem',
              height: '40px',
              mb: 2.5,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1b3e0', borderWidth: '1.5px' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7b1fa2', borderWidth: '2px' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7b1fa2', borderWidth: '2px' },
              '& .MuiSelect-icon': { color: '#7b1fa2' },
            }}
          >
            {['CREATED', 'ONGOING', 'COMPLETED', 'CANCELLED', 'ON_HOLD'].map(s => (
              <MenuItem key={s} value={s} sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {s.replace('_', ' ')}
              </MenuItem>
            ))}
          </Select>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCloseChangeStatus}
              disabled={savingStatus}
              sx={{
                borderColor: '#dee2e6', color: '#6c757d',
                textTransform: 'none', fontWeight: 600,
                borderRadius: '7px', px: 2.5,
                '&:hover': { borderColor: '#adb5bd', backgroundColor: 'rgba(0,0,0,0.03)' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveStatus}
              variant="contained"
              size="small"
              disabled={savingStatus}
              startIcon={savingStatus ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: 15 }} />}
              sx={{
                backgroundColor: '#7b1fa2 !important',
                color: 'white !important',
                textTransform: 'none', fontWeight: 700,
                borderRadius: '7px', px: 2.5,
                '&:hover': { backgroundColor: '#4a148c !important' },
                '&.Mui-disabled': { opacity: 0.55 },
              }}
            >
              Save Status
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default IVFProcedures;
